import { CalendarClock, Clapperboard, History, Loader2, Paintbrush, Pencil, Play, Search, SearchX, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import {
  archivedThumbUrl,
  archivedVideoUrl,
  discardJob,
  editSpeakerName,
  listLibrary,
  listSchedulingPosts,
  thumbUrl,
  videoUrl,
  type JobListItem,
  type ScheduledPost,
} from "../api";
import {
  extractJobIdFromVideoUrl,
  formatCardDateTime,
  scheduledSummaryForJob,
  type ScheduledSummary,
} from "../lib/scheduledTime";
import { PageHeader } from "../components/PageHeader";
import { StatusPill, type DisplayStatus } from "../components/StatusPill";
import { LibraryDateControls } from "../components/LibraryDateControls";
import {
  applyDateView,
  readSortPref,
  SORT_PREF_KEYS,
  writeSortPref,
  type DateRange,
  type SortOrder,
} from "../lib/librarySort";

interface Props {
  onOpen: (id: string) => void;
  /** Open a clip directly in the Studio design editor. */
  onOpenStudio?: (id: string) => void;
}

type StatusFilter = "all" | "ready" | "scheduled" | "published";

export function Library({ onOpen, onOpenStudio }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<JobListItem[]>([]);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>("all");
  // Sort + date range (2026-09-14). The order is a remembered per-page
  // preference so "Oldest" sticks once chosen — the operator opens this page
  // to find clips to RECYCLE, and the oldest are the candidates. The range is
  // session-only: a persisted range would silently hide clips next visit.
  const [order, setOrderState] = useState<SortOrder>(() => readSortPref(SORT_PREF_KEYS.videos));
  const setOrder = (o: SortOrder): void => {
    setOrderState(o);
    writeSortPref(SORT_PREF_KEYS.videos, o);
  };
  const [range, setRange] = useState<DateRange>({});
  // jobId → overlay status derived from cross-referencing scheduled
  // posts. "published" beats "scheduled" beats the job's own status.
  const [schedulingByJob, setSchedulingByJob] = useState<Map<string, "scheduled" | "published">>(
    () => new Map(),
  );
  // Raw scheduled posts behind the overlay map above — retained so a
  // card can show *when* a clip publishes, not just that it's scheduled.
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);

  // When the panel toggles to history mode, refetch with archives included.
  // The current renders don't change, but the new entries (archive cards)
  // appear in chronological order alongside them.
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    void Promise.all([
      listLibrary({ includeArchives: showHistory }),
      // Scheduling lookup is best-effort: a connector outage or 429 must
      // not blank the library, so we swallow errors and fall through to
      // an empty overlay map (every card renders its own status).
      listSchedulingPosts().catch(() => ({ posts: [] })),
    ]).then(([libData, schedRes]) => {
      if (cancelled) return;
      const next = new Map<string, "scheduled" | "published">();
      for (const p of schedRes.posts) {
        const jobId = extractJobIdFromVideoUrl(p.videoUrl);
        if (!jobId) continue;
        // Published wins over scheduled — once a clip has gone out on any
        // channel we want the library to read "Published" even if other
        // channels are still queued.
        if (p.status === "published") {
          next.set(jobId, "published");
        } else if (p.status === "scheduled" && next.get(jobId) !== "published") {
          next.set(jobId, "scheduled");
        }
      }
      setSchedulingByJob(next);
      setScheduledPosts(schedRes.posts);
      setItems(libData);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [showHistory]);

  const archiveCount = useMemo(() => items.filter((i) => i.archiveIdx !== undefined).length, [items]);
  const currentCount = items.length - archiveCount;

  /** Compute the on-screen status for a single library row: archive
   *  cards stay archives; otherwise scheduling overlay beats own status. */
  const displayStatusOf = (item: JobListItem): DisplayStatus => {
    if (item.archiveIdx !== undefined) return item.status;
    return schedulingByJob.get(item.id) ?? item.status;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kept = items.filter((j) => {
      if (q) {
        const hay = [j.title, j.speaker ?? "", j.id, j.sourceTitle ?? ""].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filter === "all") return true;
      // Filter pills only apply to live (non-archive) cards — archives
      // stay visible as a historical layer when history is toggled on.
      if (j.archiveIdx !== undefined) return false;
      const ds = displayStatusOf(j);
      if (filter === "scheduled") return ds === "scheduled";
      if (filter === "published") return ds === "published";
      // "ready" = a finished clip not yet bound to any scheduled post.
      return ds === "ready";
    });
    // Date axis = the date the card shows (createdAt). Applied AFTER the
    // status/search filter so the range narrows what's already visible.
    return applyDateView(kept, (j) => j.createdAt, order, range);
  }, [items, query, filter, schedulingByJob, order, range]);

  const filterCounts = useMemo(() => {
    const counts = { all: 0, ready: 0, scheduled: 0, published: 0 };
    for (const j of items) {
      if (j.archiveIdx !== undefined) continue;
      counts.all += 1;
      const ds = displayStatusOf(j);
      if (ds === "scheduled") counts.scheduled += 1;
      else if (ds === "published") counts.published += 1;
      else if (ds === "ready") counts.ready += 1;
    }
    return counts;
    // displayStatusOf reads schedulingByJob, so include it as a dep.

  }, [items, schedulingByJob]);

  // jobId → earliest upcoming publish time (+ channel count). Built once
  // per scheduling fetch; the card looks up its own entry. Only jobs with
  // at least one "scheduled" post land here.
  const scheduledByJob = useMemo(() => {
    const m = new Map<string, ScheduledSummary>();
    const jobIds = new Set<string>();
    for (const p of scheduledPosts) {
      if (p.status !== "scheduled") continue;
      const id = extractJobIdFromVideoUrl(p.videoUrl);
      if (id) jobIds.add(id);
    }
    for (const id of jobIds) {
      const s = scheduledSummaryForJob(scheduledPosts, id);
      if (s) m.set(id, s);
    }
    return m;
  }, [scheduledPosts]);

  /** A wiped clip disappears with every archived version it owned — the
   *  server's discardJob(wipe) removed them all, so the list must too. */
  const handleDeleted = (id: string): void => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "150px" : "190px"}, 1fr))`,
    gap: 14,
  };

  return (
    <div>
      <PageHeader
        eyebrow="Library"
        title="Every clip you've ever made."
        subtitle="Search, re-open, or copy social captions from finished jobs. Items appear here as soon as the .mp4 lands in out/."
      />

      {/* One toolbar row: filters · count · history · search. Sticky on
          desktop so the controls stay reachable while scanning a 70-clip
          wall. NOT sticky on mobile: the app's own sticky header (z 30)
          would pin on top of it, and the wrapped rows would eat ~200px of
          a phone viewport. */}
      <div
        style={{
          position: isMobile ? "static" : "sticky",
          top: 0,
          // Above the cards' own overlays (z 5) so an open confirm panel
          // scrolling underneath can't paint over the toolbar.
          zIndex: 10,
          background: "rgba(10,10,11,.92)",
          backdropFilter: isMobile ? undefined : "blur(8px)",
          WebkitBackdropFilter: isMobile ? undefined : "blur(8px)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          padding: "10px 0 14px",
          marginBottom: 14,
        }}
      >
        {([
          { id: "all" as StatusFilter, label: "All", count: filterCounts.all },
          { id: "ready" as StatusFilter, label: "Ready", count: filterCounts.ready },
          { id: "scheduled" as StatusFilter, label: "Scheduled", count: filterCounts.scheduled },
          { id: "published" as StatusFilter, label: "Published", count: filterCounts.published },
        ]).map(({ id, label, count }) => (
          <button
            key={id}
            type="button"
            className="ql-chip"
            aria-pressed={filter === id}
            onClick={() => setFilter(id)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              border: `1px solid ${filter === id ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
              background: filter === id ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
              color: filter === id ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {label}
            {/* Counts only once real data is in — "0" over skeletons that
                promise content would be a lie for a beat. */}
            {loaded && (
              <span
                style={{
                  fontSize: 10,
                  fontFamily: "var(--ql-font-mono)",
                  color: filter === id ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                  opacity: 0.85,
                }}
              >
                {count}
              </span>
            )}
          </button>
        ))}

        <LibraryDateControls
          order={order}
          onOrder={setOrder}
          range={range}
          onRange={setRange}
          dateLabel="rendered"
          compact={isMobile}
        />

        <span style={{ flex: 1, minWidth: 8 }} />

        <span
          style={{
            fontSize: 11.5,
            color: "var(--ql-fg-subtle)",
            fontFamily: "var(--ql-font-mono)",
            whiteSpace: "nowrap",
          }}
        >
          {loaded ? (
            <>
              {currentCount} {currentCount === 1 ? "clip" : "clips"}
              {showHistory && archiveCount > 0 && <> · {archiveCount} archived</>}
            </>
          ) : (
            "loading…"
          )}
        </span>

        <button
          type="button"
          className="ql-chip"
          aria-pressed={showHistory}
          onClick={() => setShowHistory((v) => !v)}
          title={showHistory ? "Hide past renders" : "Include past renders from re-runs"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 999,
            border: showHistory
              ? "1px solid rgba(255,213,0,.30)"
              : "1px solid rgba(255,255,255,.08)",
            background: showHistory ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.02)",
            color: showHistory ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
            fontFamily: "inherit",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <History size={12} strokeWidth={2} />
          {showHistory ? "Hide history" : "Show history"}
        </button>

        <div style={{ position: "relative", width: isMobile ? "100%" : undefined }}>
          <Search
            size={14}
            strokeWidth={2}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ql-fg-subtle)",
              pointerEvents: "none",
            }}
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by speaker, source, keyword…"
            aria-label="Search clips"
            style={{
              padding: "8px 14px 8px 36px",
              borderRadius: 12,
              width: isMobile ? "100%" : 280,
              background: "var(--ql-input)",
              border: "1px solid rgba(255,255,255,.08)",
              color: "var(--ql-fg)",
              fontSize: 13,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {!loaded ? (
        <div style={gridStyle} aria-hidden>
          {Array.from({ length: 12 }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        items.length === 0 ? (
          <div style={emptyStyle}>
            <Clapperboard size={26} strokeWidth={1.6} style={{ color: "var(--ql-fg-subtle)" }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ql-fg)" }}>
              No finished clips yet
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", maxWidth: 380, lineHeight: 1.55 }}>
              Render one from the New job screen (or let a scan fill the
              Backlog) and it lands here the moment the .mp4 is done.
            </div>
          </div>
        ) : (
          <div style={emptyStyle}>
            <SearchX size={26} strokeWidth={1.6} style={{ color: "var(--ql-fg-subtle)" }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ql-fg)" }}>
              {query.trim() ? <>Nothing matches “{query.trim()}”</> : "Nothing in this filter"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {query.trim() && (
                <button type="button" className="ql-chip" onClick={() => setQuery("")} style={emptyActionStyle}>
                  Clear search
                </button>
              )}
              {filter !== "all" && (
                <button type="button" className="ql-chip" onClick={() => setFilter("all")} style={emptyActionStyle}>
                  Show all clips
                </button>
              )}
            </div>
          </div>
        )
      ) : (
        <div style={gridStyle}>
          {filtered.map((j, i) => (
            <LibraryCard
              key={`${j.id}::${j.archiveIdx ?? "current"}`}
              item={j}
              tourAnchor={i === 0 ? "library-card" : undefined}
              displayStatus={displayStatusOf(j)}
              scheduledSummary={scheduledByJob.get(j.id) ?? null}
              onOpen={onOpen}
              onOpenStudio={onOpenStudio}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One clip tile. Two flavours behind the same component:
 *   - `current`: the latest render. Click opens the JobDetail screen.
 *   - `archive` (archiveIdx set): a past render from the archive. Click
 *     opens an inline preview right inside the card — a JobDetail page
 *     for an archived index would be misleading because the editing
 *     controls there only act on the current render.
 *
 * The visual shell (border, bg, radius) lives on the WRAPPER so the CSS
 * hover choreography (.ql-vcard in app.css) and the two overlays (speaker
 * editor, delete confirm) share its geometry; the inner <button> is a
 * transparent hit area.
 */
function LibraryCard({
  item,
  displayStatus,
  scheduledSummary,
  onOpen,
  onOpenStudio,
  onDeleted,
  tourAnchor,
}: {
  item: JobListItem;
  displayStatus: DisplayStatus;
  scheduledSummary: ScheduledSummary | null;
  onOpen: (id: string) => void;
  onOpenStudio?: (id: string) => void;
  onDeleted: (id: string) => void;
  /** `data-tour` anchor for the guided tour — set on the FIRST card only. */
  tourAnchor?: string;
}): React.JSX.Element {
  const [thumbErrored, setThumbErrored] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  // One overlay at a time: the speaker-name editor or the delete confirm.
  const [overlay, setOverlay] = useState<"none" | "name" | "delete">("none");
  // Focus returns to the card when an overlay closes (keyboard users would
  // otherwise be dropped on <body>).
  const cardBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeOverlay = (): void => {
    setOverlay("none");
    cardBtnRef.current?.focus();
  };

  // Mid-pipeline (a trim / speaker re-render in flight): the child is still
  // writing work/ and out/ — a hard wipe now would race it (same guard the
  // Render queue's discard uses). No delete until it settles.
  const inFlight =
    item.status !== "ready" && item.status !== "failed" && item.status !== "incomplete";

  // Inline "edit speaker name" — a rendered card only. The editor is a sibling
  // overlay of the card button (never a nested <input> inside a <button>, which
  // is invalid + unfocusable). Save locks the operator name and re-renders the
  // card; we then open JobDetail so the operator watches the re-render live.
  const [nameDraft, setNameDraft] = useState(item.speaker ?? "");
  const [savingName, setSavingName] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const nameChanged = nameDraft.trim() !== "" && nameDraft.trim() !== (item.speaker ?? "").trim();

  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const openNameEditor = (): void => {
    setNameDraft(item.speaker ?? "");
    setNameErr(null);
    setOverlay("name");
  };
  const cancelNameEditor = (): void => {
    if (savingName) return;
    setNameErr(null);
    closeOverlay();
  };
  const saveName = async (): Promise<void> => {
    if (savingName || !nameChanged) return;
    setSavingName(true);
    setNameErr(null);
    try {
      await editSpeakerName(item.id, nameDraft.trim());
      // The clip is now re-rendering — land on JobDetail to watch it live (the
      // Library list isn't live-polled).
      onOpen(item.id);
    } catch (err) {
      setNameErr(err instanceof Error ? err.message : String(err));
      setSavingName(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleting || inFlight) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await discardJob(item.id, true);
      onDeleted(item.id);
    } catch (err) {
      setDeleteErr(err instanceof Error ? err.message : String(err));
      setDeleting(false);
    }
  };

  const isArchive = item.archiveIdx !== undefined;
  const thumb = isArchive
    ? archivedThumbUrl(item.id, item.archiveIdx!)
    : thumbUrl(item.id);
  const video = isArchive
    ? archivedVideoUrl(item.id, item.archiveIdx!)
    : videoUrl(item.id);

  const handleClick = (): void => {
    if (isArchive) {
      // Toggle inline preview — the JobDetail page only acts on the
      // current render, opening it for an archive would mislead the
      // operator about which version their edits are touching.
      setShowPlayer((v) => !v);
    } else {
      onOpen(item.id);
    }
  };

  return (
    <div
      className={isArchive ? "ql-vcard ql-vcard--archive" : "ql-vcard"}
      data-tour={tourAnchor}
      style={{
        position: "relative",
        background: "var(--ql-card)",
        borderRadius: 18,
        border: isArchive
          ? "1px solid rgba(255,213,0,.18)"
          : "1px solid rgba(255,255,255,.06)",
        overflow: "hidden",
      }}
    >
      <button
        ref={cardBtnRef}
        onClick={handleClick}
        // Behind an open overlay the scrim blocks the mouse; disabling also
        // removes the card from the TAB order so keyboard users can't open
        // JobDetail of a clip that's mid-delete.
        disabled={overlay !== "none"}
        title={isArchive ? "Preview this past render" : "Open clip"}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          background: "transparent",
          border: "none",
        }}
      >
        <div
          style={{
            aspectRatio: "9/16",
            background: "linear-gradient(135deg,#1a1f2e 0%,#0a0a0b 60%,#1f1815 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {showPlayer && isArchive ? (
            <video
              controls
              autoPlay
              preload="metadata"
              src={video}
              style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : !thumbErrored ? (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              onError={() => setThumbErrored(true)}
              className="ql-vcard-zoom"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle at 50% 35%, rgba(255,213,0,.05), transparent 55%)",
              }}
            />
          )}

          {/* Legibility scrim: keeps the duration chip and status pills
              readable over bright footage without dimming the frame. */}
          {!showPlayer && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg, rgba(0,0,0,.28) 0%, transparent 22%, transparent 64%, rgba(0,0,0,.45) 100%)",
                pointerEvents: "none",
              }}
            />
          )}

          {/* Play affordance — hover/focus-revealed on desktop, always on touch. */}
          {!showPlayer && (
            <div
              className="ql-vcard-reveal"
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%,-50%)",
                width: 42,
                height: 42,
                borderRadius: 999,
                background: "rgba(0,0,0,.55)",
                border: "1px solid rgba(255,255,255,.22)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Play size={15} strokeWidth={2.5} style={{ marginLeft: 2 }} />
            </div>
          )}

          <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6 }}>
            {!isArchive && item.avSync && (
              <span
                title={
                  item.avSync.desync
                    ? `Picture drift — ${item.avSync.mismatches}/${item.avSync.checked} checked frames show the wrong person for the audio`
                    : "Picture checked — the on-screen person matched the speaker (two-person shots can't always be verified)"
                }
                style={{
                  padding: "3px 8px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  background: item.avSync.desync ? "rgba(248,113,113,.20)" : "rgba(74,222,128,.18)",
                  color: item.avSync.desync ? "#FCA5A5" : "#86EFAC",
                  border: `1px solid ${item.avSync.desync ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
                }}
              >
                {item.avSync.desync ? "Picture ⚠" : "Picture ✓"}
              </span>
            )}
            {isArchive ? (
              <span
                style={{
                  padding: "3px 8px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  background: "rgba(255,213,0,.20)",
                  color: "var(--ql-yellow)",
                  border: "1px solid rgba(255,213,0,.30)",
                }}
              >
                v{item.archiveIdx} · archive
              </span>
            ) : (
              <StatusPill status={displayStatus} progress={item.progress} />
            )}
          </div>
          {item.durationSec ? (
            <div
              style={{
                position: "absolute",
                bottom: 10,
                right: 10,
                padding: "3px 8px",
                borderRadius: 999,
                background: "rgba(0,0,0,.6)",
                fontSize: 10,
                color: "#fff",
                fontFamily: "var(--ql-font-mono)",
                fontWeight: 600,
              }}
            >
              {formatDuration(item.durationSec)}
            </div>
          ) : null}
        </div>
        <div style={{ padding: "12px 14px 13px" }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
              marginBottom: 7,
              minHeight: "2.8em",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              color: "var(--ql-gray-200)",
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ql-fg-muted)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                overflow: "hidden",
                minWidth: 0,
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.speaker || "—"}
              </span>
              {!isArchive && (
                <span
                  role="button"
                  tabIndex={overlay === "none" ? 0 : -1}
                  title="Edit speaker name"
                  aria-label="Edit speaker name"
                  onClick={(e) => { e.stopPropagation(); openNameEditor(); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); openNameEditor(); }
                  }}
                  style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "var(--ql-fg-subtle)",
                    padding: 2,
                    borderRadius: 6,
                  }}
                >
                  <Pencil size={11} strokeWidth={2} />
                </span>
              )}
            </span>
            <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 10.5, whiteSpace: "nowrap" }}>
              {formatDate(item.createdAt)}
            </span>
          </div>
          {!isArchive && displayStatus === "scheduled" && scheduledSummary && (
            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                color: "var(--ql-fg-muted)",
              }}
              title={`Publishes ${formatCardDateTime(scheduledSummary.at)}${
                scheduledSummary.count > 1 ? ` · on ${scheduledSummary.count} channels` : ""
              }`}
            >
              <CalendarClock
                size={12}
                strokeWidth={2}
                style={{ flexShrink: 0, color: "var(--ql-yellow)" }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                Publishes {formatCardDateTime(scheduledSummary.at)}
              </span>
              {scheduledSummary.count > 1 && (
                <span
                  style={{
                    flexShrink: 0,
                    fontFamily: "var(--ql-font-mono)",
                    fontSize: 9,
                    fontWeight: 700,
                    color: "var(--ql-yellow)",
                    background: "rgba(255,213,0,.12)",
                    border: "1px solid rgba(255,213,0,.25)",
                    borderRadius: 999,
                    padding: "1px 5px",
                  }}
                  title={`Also on ${scheduledSummary.count - 1} more channel${
                    scheduledSummary.count - 1 === 1 ? "" : "s"
                  }`}
                >
                  +{scheduledSummary.count - 1}
                </span>
              )}
            </div>
          )}
        </div>
      </button>

      {/* Delete trigger — sibling of the card button (a nested button would be
          invalid HTML). Hover/focus-revealed on desktop, always on touch.
          Hidden while a re-render is in flight: the child is still writing
          files a hard wipe would race (same rule as the queue's discard).
          Space activates on KEYUP so the keystroke can't ghost-press the
          confirm panel's autofocused "Keep" button. */}
      {!isArchive && !inFlight && overlay === "none" && (
        <span
          role="button"
          tabIndex={0}
          className="ql-vcard-reveal ql-vcard-del"
          title="Delete clip…"
          aria-label={`Delete clip ${item.title}`}
          onClick={(e) => { e.stopPropagation(); setDeleteErr(null); setOverlay("delete"); }}
          onKeyDown={(e) => {
            if (e.key === " ") e.preventDefault(); // no page scroll; fires on keyup
            if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); setDeleteErr(null); setOverlay("delete"); }
          }}
          onKeyUp={(e) => {
            if (e.key === " ") { e.preventDefault(); e.stopPropagation(); setDeleteErr(null); setOverlay("delete"); }
          }}
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 2,
            width: 28,
            height: 28,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "rgba(0,0,0,.55)",
            border: "1px solid rgba(255,255,255,.20)",
            color: "rgba(255,255,255,.85)",
          }}
        >
          <Trash2 size={13} strokeWidth={2} />
        </span>
      )}

      {/* Open in Studio — hover-revealed next to delete (spec 2026-07-18 §4:
          the Studio is reachable from the Library, not only JobDetail).
          Only on clips WITH a finished render — failed/incomplete clips lack
          the captions + source the Studio needs and would dead-end. */}
      {!isArchive && !inFlight && overlay === "none" && onOpenStudio
        && (displayStatus === "ready" || displayStatus === "scheduled" || displayStatus === "published") && (
        <span
          role="button"
          tabIndex={0}
          className="ql-vcard-reveal"
          title="Open in Studio — edit this clip's caption design on a live preview"
          aria-label={`Open ${item.title} in Studio`}
          onClick={(e) => { e.stopPropagation(); onOpenStudio(item.id); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpenStudio(item.id); }
          }}
          style={{
            position: "absolute",
            top: 10,
            left: 44,
            zIndex: 2,
            width: 28,
            height: 28,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "rgba(0,0,0,.55)",
            border: "1px solid rgba(255,213,0,.35)",
            color: "var(--ql-yellow)",
          }}
        >
          <Paintbrush size={13} strokeWidth={2} />
        </span>
      )}

      {!isArchive && overlay === "name" && (
        // Full-card scrim so a stray click on the thumbnail can't navigate away
        // mid-edit; the editor panel sits at the bottom. stopPropagation on the
        // whole overlay keeps every click off the underlying card button.
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 18,
            background: "rgba(6,8,12,.72)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            zIndex: 5,
          }}
        >
          <div
            style={{
              padding: 12,
              background: "rgba(10,12,18,.98)",
              borderTop: "1px solid rgba(255,213,0,.35)",
              borderBottomLeftRadius: 18,
              borderBottomRightRadius: 18,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "var(--ql-yellow)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Edit speaker name
            </div>
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveName();
                if (e.key === "Escape") cancelNameEditor();
              }}
              placeholder="e.g. Dr Julie Smith"
              aria-label="Speaker name"
              disabled={savingName}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                borderRadius: 8,
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(255,255,255,.15)",
                color: "var(--ql-fg)",
                fontFamily: "inherit",
                fontSize: 13,
              }}
            />
            {nameErr && (
              <div style={{ fontSize: 11, color: "var(--ql-danger)", fontFamily: "var(--ql-font-mono)" }}>
                {nameErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => { void saveName(); }}
                disabled={savingName || !nameChanged}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,213,0,.45)",
                  background: "rgba(255,213,0,.14)",
                  color: "var(--ql-yellow)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: savingName || !nameChanged ? "not-allowed" : "pointer",
                  opacity: savingName || !nameChanged ? 0.5 : 1,
                }}
              >
                {savingName ? <Loader2 size={13} className="ql-spin" /> : <Pencil size={13} strokeWidth={2} />}
                {savingName ? "Saving…" : "Save & re-render"}
              </button>
              <button
                onClick={cancelNameEditor}
                disabled={savingName}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.15)",
                  background: "transparent",
                  color: "var(--ql-fg-muted)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: savingName ? "not-allowed" : "pointer",
                  opacity: savingName ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!isArchive && overlay === "delete" && (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !deleting) { setDeleteErr(null); closeOverlay(); }
          }}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 18,
            background: "rgba(6,8,12,.78)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            zIndex: 5,
          }}
        >
          <div
            style={{
              padding: 12,
              background: "rgba(10,12,18,.98)",
              borderTop: "1px solid rgba(248,113,113,.40)",
              borderBottomLeftRadius: 18,
              borderBottomRightRadius: 18,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "var(--ql-danger)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Delete this clip?
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--ql-fg-muted)" }}>
              Removes the video, captions, render history and files, here and
              in storage. Can't be undone.
            </div>
            {(displayStatus === "scheduled" || displayStatus === "published") && (
              <div style={{ fontSize: 11, lineHeight: 1.5, color: "var(--ql-warning)" }}>
                {displayStatus === "scheduled"
                  ? "It's scheduled to publish — that post will lose its video."
                  : "It's already published — the live post keeps its own copy; only the source files here are removed."}
              </div>
            )}
            {deleteErr && (
              <div style={{ fontSize: 11, color: "var(--ql-danger)", fontFamily: "var(--ql-font-mono)" }}>
                {deleteErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => { void confirmDelete(); }}
                disabled={deleting}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(248,113,113,.45)",
                  background: "rgba(248,113,113,.14)",
                  color: "var(--ql-danger)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: deleting ? "not-allowed" : "pointer",
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? <Loader2 size={13} className="ql-spin" /> : <Trash2 size={13} strokeWidth={2} />}
                {deleting ? "Deleting…" : "Delete forever"}
              </button>
              <button
                onClick={() => { if (!deleting) { setDeleteErr(null); closeOverlay(); } }}
                disabled={deleting}
                autoFocus
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.15)",
                  background: "transparent",
                  color: "var(--ql-fg-muted)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: deleting ? "not-allowed" : "pointer",
                  opacity: deleting ? 0.5 : 1,
                }}
              >
                Keep
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Placeholder tile matching the card silhouette — the grid keeps its
 *  shape while loading instead of flashing a spinner. */
function SkeletonCard(): React.JSX.Element {
  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,.05)",
        overflow: "hidden",
        background: "var(--ql-card)",
      }}
    >
      <div className="ql-skel" style={{ aspectRatio: "9/16" }} />
      <div style={{ padding: "12px 14px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="ql-skel" style={{ height: 12, borderRadius: 6, width: "90%" }} />
        <div className="ql-skel" style={{ height: 12, borderRadius: 6, width: "60%" }} />
        <div className="ql-skel" style={{ height: 10, borderRadius: 6, width: "40%" }} />
      </div>
    </div>
  );
}

const emptyStyle: React.CSSProperties = {
  background: "var(--ql-card)",
  border: "1px dashed rgba(255,255,255,.10)",
  borderRadius: 18,
  padding: "56px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  textAlign: "center",
};

const emptyActionStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.03)",
  color: "var(--ql-fg-muted)",
  fontSize: 11.5,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return sameDay
    ? `${hh}:${mm}`
    : `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
}
